/**
 * Validation Utility Functions
 * Centralized validation logic for all forms in the application
 */

/**
 * Normalizes a URL by adding https:// if protocol is missing
 * @param {string} url - The URL to normalize
 * @returns {string} Normalized URL with protocol
 */
export const normalizeURL = (url) => {
  if (!url || url.trim() === '') {
    return '';
  }

  let normalized = url.trim();

  // If URL already has a protocol, return as is
  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }

  // Add https:// to the beginning
  normalized = 'https://' + normalized;

  return normalized;
};

/**
 * Validates URL format
 * @param {string} url - The URL to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateURL = (url) => {
  if (!url || url.trim() === '') {
    return { isValid: false, error: 'URL is required' };
  }

  // Check for common typos (comma instead of dot)
  if (url.includes(',')) {
    return { isValid: false, error: 'Invalid URL format. Did you mean to use a dot "." instead of comma ","?' };
  }

  // Must start with http:// or https://
  if (!url.match(/^https?:\/\//i)) {
    return { isValid: false, error: 'URL must start with http:// or https://' };
  }

  try {
    const urlObj = new URL(url);

    // Check if it has a valid hostname
    if (!urlObj.hostname || urlObj.hostname === 'localhost') {
      return { isValid: false, error: 'Please enter a valid domain name' };
    }

    // Check if hostname has at least one dot (e.g., example.com)
    if (!urlObj.hostname.includes('.')) {
      return { isValid: false, error: 'Please enter a valid domain name (e.g., example.com)' };
    }

    return { isValid: true, error: '' };
  } catch (e) {
    return { isValid: false, error: 'Please enter a valid URL (e.g., https://www.example.com)' };
  }
};

/**
 * Validates email format
 * @param {string} email - The email to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }

  // RFC 5322 compliant email regex (simplified but robust)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // Additional checks
  if (email.includes('..')) {
    return { isValid: false, error: 'Email cannot contain consecutive dots' };
  }

  if (email.startsWith('.') || email.endsWith('.')) {
    return { isValid: false, error: 'Email cannot start or end with a dot' };
  }

  return { isValid: true, error: '' };
};

/**
 * Validates international phone number (flexible format)
 * @param {string} phone - The phone number to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePhone = (phone) => {
  if (!phone || phone.trim() === '') {
    return { isValid: false, error: 'Phone number is required' };
  }

  // Remove spaces, hyphens, parentheses, and plus sign for validation
  const cleanPhone = phone.replace(/[\s\-()]/g, '');

  // Check if it contains only digits and optional leading +
  if (!/^\+?\d+$/.test(cleanPhone)) {
    return { isValid: false, error: 'Phone number must contain only digits, spaces, hyphens, or parentheses' };
  }

  // Remove + for length check
  const digitsOnly = cleanPhone.replace(/\+/g, '');

  // International phone numbers are typically 7-15 digits
  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return { isValid: false, error: 'Phone number must be between 7 and 15 digits' };
  }

  return { isValid: true, error: '' };
};

/**
 * Validates LinkedIn profile URL
 * @param {string} url - The LinkedIn URL to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateLinkedInURL = (url) => {
  if (!url || url.trim() === '') {
    return { isValid: false, error: 'LinkedIn URL is required' };
  }

  // First validate it's a proper URL
  const urlValidation = validateURL(url);
  if (!urlValidation.isValid) {
    return urlValidation;
  }

  try {
    const urlObj = new URL(url);

    // Check if it's a LinkedIn domain
    if (!urlObj.hostname.includes('linkedin.com')) {
      return { isValid: false, error: 'Please enter a valid LinkedIn URL (must be from linkedin.com)' };
    }

    // Flexible validation - just ensure it's from LinkedIn domain
    // No longer requiring specific paths like /in/ or /company/
    return { isValid: true, error: '' };
  } catch (e) {
    return { isValid: false, error: 'Please enter a valid LinkedIn URL' };
  }
};

/**
 * Validates Twitter/X profile URL (links only, no handles)
 * @param {string} url - The Twitter URL to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateTwitterURL = (url) => {
  if (!url || url.trim() === '') {
    return { isValid: false, error: 'Twitter/X profile URL is required' };
  }

  // First validate it's a proper URL
  const urlValidation = validateURL(url);
  if (!urlValidation.isValid) {
    return urlValidation;
  }

  try {
    const urlObj = new URL(url);

    // Check if it's a Twitter/X domain
    if (!urlObj.hostname.includes('twitter.com') && !urlObj.hostname.includes('x.com')) {
      return { isValid: false, error: 'Please enter a valid Twitter/X URL (twitter.com or x.com)' };
    }

    return { isValid: true, error: '' };
  } catch (e) {
    return { isValid: false, error: 'Please enter a valid Twitter/X URL' };
  }
};

/**
 * Validates Facebook profile URL
 * @param {string} url - The Facebook URL to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateFacebookURL = (url) => {
  if (!url || url.trim() === '') {
    return { isValid: false, error: 'Facebook URL is required' };
  }

  // First validate it's a proper URL
  const urlValidation = validateURL(url);
  if (!urlValidation.isValid) {
    return urlValidation;
  }

  try {
    const urlObj = new URL(url);

    // Check if it's a Facebook domain
    if (!urlObj.hostname.includes('facebook.com') && !urlObj.hostname.includes('fb.com')) {
      return { isValid: false, error: 'Please enter a valid Facebook URL (facebook.com or fb.com)' };
    }

    return { isValid: true, error: '' };
  } catch (e) {
    return { isValid: false, error: 'Please enter a valid Facebook URL' };
  }
};

/**
 * Validates text field with minimum length
 * @param {string} text - The text to validate
 * @param {number} minLength - Minimum required length
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateTextMinLength = (text, minLength, fieldName = 'Field') => {
  if (!text || text.trim() === '') {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const trimmedText = text.trim();
  if (trimmedText.length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} characters (currently ${trimmedText.length})`
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validates text field with minimum and maximum length
 * @param {string} text - The text to validate
 * @param {number} minLength - Minimum required length
 * @param {number} maxLength - Maximum allowed length
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateTextLength = (text, minLength, maxLength, fieldName = 'Field') => {
  if (!text || text.trim() === '') {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const trimmedText = text.trim();

  if (trimmedText.length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} characters (currently ${trimmedText.length})`
    };
  }

  if (trimmedText.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must not exceed ${maxLength} characters (currently ${trimmedText.length})`
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validates required field
 * @param {string} value - The value to validate
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateRequired = (value, fieldName = 'Field') => {
  if (!value || value.toString().trim() === '') {
    return { isValid: false, error: `${fieldName} is required` };
  }
  return { isValid: true, error: '' };
};

/**
 * Helper function to validate multiple fields at once
 * @param {Object} validations - Object with field names as keys and validation results as values
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateAll = (validations) => {
  const errors = {};
  let isValid = true;

  Object.keys(validations).forEach(fieldName => {
    const validation = validations[fieldName];
    if (!validation.isValid) {
      errors[fieldName] = validation.error;
      isValid = false;
    }
  });

  return { isValid, errors };
};
